import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppleDeviceService {
  private readonly STORAGE_KEY_PERMANENT = 'apple_app_banner_dismissed';
  private readonly STORAGE_KEY_LATER = 'apple_app_banner_later';
  private readonly LATER_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  private readonly APP_STORE_URL = 'https://apps.apple.com/app/thinvoices/id6754467876';
  
  private _isAppleDevice$ = new BehaviorSubject<boolean>(false);
  private _showBanner$ = new BehaviorSubject<boolean>(false);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.detectAppleDevice();
      this.checkBannerVisibility();
    }
  }

  /**
   * Detect if the user is on an Apple device
   */
  private detectAppleDevice(): void {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Check for iOS devices (iPhone, iPad, iPod)
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    
    // Check for Mac (excluding iOS devices)
    const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent) && !isIOS;
    
    // Check for Safari on Mac (more reliable detection)
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isMacSafari = isMac && isSafari;
    
    const isApple = isIOS || isMac || isMacSafari;
    
    this._isAppleDevice$.next(isApple);
  }

  /**
   * Check if banner should be shown.
   * Only after login on dashboard routes — never on auth pages.
   */
  private checkBannerVisibility(): void {
    const isPermanentlyDismissed = localStorage.getItem(this.STORAGE_KEY_PERMANENT) === 'true';
    const isApple = this._isAppleDevice$.value;

    const token = localStorage.getItem('token');
    const isLoggedIn = !!token;

    const userTypeRaw = localStorage.getItem('user_type');
    const userType = userTypeRaw ? parseInt(userTypeRaw, 10) : null;
    const isAccountant = userType === 300 || userType === 301;

    if (!isApple || !isLoggedIn || isPermanentlyDismissed || isAccountant || this.isOnAuthRoute()) {
      this._showBanner$.next(false);
      return;
    }

    // Check if "Later" was clicked and if the delay period has passed
    const laterTimestamp = localStorage.getItem(this.STORAGE_KEY_LATER);
    if (laterTimestamp) {
      const laterTime = parseInt(laterTimestamp, 10);
      const now = Date.now();
      const timePassed = now - laterTime;
      
      if (timePassed < this.LATER_DURATION) {
        // Still within the "later" period, don't show
        this._showBanner$.next(false);
        return;
      } else {
        // "Later" period expired, show again and clear the timestamp
        localStorage.removeItem(this.STORAGE_KEY_LATER);
      }
    }
    
    this._showBanner$.next(true);
  }

  /**
   * Get observable for Apple device detection
   */
  get isAppleDevice$(): Observable<boolean> {
    return this._isAppleDevice$.asObservable();
  }

  /**
   * Get observable for banner visibility
   */
  get showBanner$(): Observable<boolean> {
    return this._showBanner$.asObservable();
  }

  /**
   * Get App Store URL
   */
  get appStoreUrl(): string {
    return this.APP_STORE_URL;
  }

  /**
   * Dismiss the banner permanently (don't show again)
   */
  dismissBannerPermanently(): void {
    localStorage.setItem(this.STORAGE_KEY_PERMANENT, 'true');
    localStorage.removeItem(this.STORAGE_KEY_LATER); // Clear any "later" timestamp
    this._showBanner$.next(false);
  }

  /**
   * Dismiss the banner temporarily (show again after 7 days)
   */
  dismissBannerLater(): void {
    localStorage.setItem(this.STORAGE_KEY_LATER, Date.now().toString());
    this._showBanner$.next(false);
  }

  /**
   * Re-check banner visibility (e.g. after login when user_type becomes available).
   * Use this after storing user_type so accountant users (300) never see the banner.
   */
  recheckBannerVisibility(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.checkBannerVisibility();
    }
  }

  /**
   * Reset dismissal (for testing purposes)
   */
  resetDismissal(): void {
    localStorage.removeItem(this.STORAGE_KEY_PERMANENT);
    localStorage.removeItem(this.STORAGE_KEY_LATER);
    this.checkBannerVisibility();
  }

  /**
   * Check if currently on Apple device (synchronous)
   */
  isAppleDevice(): boolean {
    return this._isAppleDevice$.value;
  }

  /** Never show app-download banner on login/OTP/auth screens. */
  private isOnAuthRoute(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hash = (window.location.hash || '').toLowerCase();
    return /^#\/(login|otp|register|resend-otp|forgot-password)(\/|$|\?)/.test(hash);
  }
}
